import React, { useEffect, useState, useMemo, useRef } from "react";
import { Spin, Typography, message } from "antd";
import './PreferredTime.css';
import dayjs from "dayjs";
import moment from "moment";
import { genericApiCall, spacerData } from "./CommonApiCallCalender";
import { Config } from "../Common/Config";
import useDebounce from "../Common/useDebounce";
import DatePickerPopover from "../Shared/DatePickerPopover";
import LocationSearchSelect from "../Shared/LocationSearchSelect";
import { filterByNonGpProductId } from "../Common/CommonFunction";
import { formatString } from "../Common/ColorCode";
import { pushTraceLogEvent, logInteraction } from "./sessionEventPush";

const CalendarIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#008385"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const LocationIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#008385"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const { Title } = Typography;
const MAX_PAGINATION_DAYS = 30;
const FILTER_OPTS = [
  { val: 'all', label: 'All day' },
  { val: 'am',  label: 'AM' },
  { val: 'pm',  label: 'PM' },
];
const SLOT_GROUPS = [
  { key: 'AM', from: '00:00', to: '11:59' },
  { key: 'PM', from: '12:00', to: '23:59' },
];

function applyTimeFilter(slots, filter) {
  if (filter === 'am') return slots.filter(s => s.start_time < '12:00');
  if (filter === 'pm') return slots.filter(s => s.start_time >= '12:00');
  return slots;
}

function groupSlotsByAmPm(slots) {
  return SLOT_GROUPS.map(g => ({
    label: g.key,
    slots: slots.filter(s => s.start_time >= g.from && s.start_time <= g.to),
  })).filter(g => g.slots.length > 0);
}

const PreferredTime = (props) => {
  const [locationPermission, setLocationPermission] = useState(() => localStorage.getItem('locationPermission'));
  const [messageApi, contextHolder] = message.useMessage();

  // ── Clinic list / hover state ───────────────────────────────────────────────
  const [selectedClinicsNew, setSelectedClinicsNew] = useState([]);
  const [hoveredClinic, setHoveredClinic] = useState(null);

  // ── Booking selection: only ONE slot can ever be booked at a time — picking
  // a new slot (any clinic, any date) replaces it. A clinic is "selected"
  // implicitly, by having the booked slot tied to it.
  const [bookedSlot, setBookedSlot] = useState(null); // { id, dateStr, clinicId, start_time, end_time, info } | null
  const [openClinicCards, setOpenClinicCards] = useState(new Set());
  const [blockingSlot, setBlockingSlot] = useState(false);

  // ── API / loading state ────────────────────────────────────────────────────
  const [loading, setloading] = useState(false);
  const [loadingTime, setloadingTime] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showPreferredTimeSlot, setshowPreferredTimeSlot] = useState({});
  const [metaData, setmetaData] = useState(null);
  // Full set of bookable dates from the API (not limited to whatever page(s)
  // have been loaded so far) — drives the "Select dates" picker's enabled days.
  // There's no "checked clinics" concept on this screen (unlike ClinicBooking),
  // so unlike there this stays a single global list, not scoped per clinic.
  const [allAvailableDates, setAllAvailableDates] = useState([]);

  // ── Location search state ──────────────────────────────────────────────────
  const [locationCoords, setlocationCoords] = useState(null);
  const [searchBylocationName, setsearchBylocationName] = useState('');
  const [searchBylocationOption, setsearchBylocationOption] = useState([]);
  const [loadingLocation, setloadingLocation] = useState(false);

  // ── Date filter state ──────────────────────────────────────────────────────
  const [chosenDates, setChosenDates] = useState(new Set());
  const [dpOpen, setDpOpen] = useState(false);
  const [openDates, setOpenDates] = useState(new Set());
  const [dateFilters, setDateFilters] = useState({});

  const anyFilterActive = useMemo(() => Object.values(dateFilters).some(Boolean), [dateFilters]);

  const handleEnableLocation = () => {
    logInteraction("enable_location_clicked", {});
    if (!navigator.geolocation) {
      messageApi.error({ content: 'Geolocation is not supported by this browser.' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        localStorage.setItem('locationPermission', 'granted');
        setLocationPermission('granted');
        logInteraction("location_permission_granted", {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        const coords = { lat: position.coords.latitude, lng: position.coords.longitude };
        setlocationCoords(coords);
        getAllPreferedTimeSlot(1, true, coords);
      },
      (error) => {
        pushTraceLogEvent({
          event: "location_permission_denied",
          payload: { message: error.message },
          step_status: "failed",
        });
        messageApi.error({ content: `Unable to retrieve location: ${error.message}` });
      }
    );
  };

  const debouncedInputValueData = useDebounce(searchBylocationName, 800);

  // ── Booking summary chips ──────────────────────────────────────────────────
  let tempDataForDuration = [];
  if (props.showAppoinmentData && props.showAppoinmentData.duration) {
    tempDataForDuration.push({
      key: 'Duration',
      data: props.data && props.data.durationObjectById && props.showAppoinmentData.duration && props.data.durationObjectById[props.showAppoinmentData.duration]
        ? props.data.durationObjectById[props.showAppoinmentData.duration].duration + ' minutes'
        : '15 minutes'
    });
  }
  const _ptCat = props.showAppoinmentData?.clinic?.productCategory;
  const _ptProd = props.showAppoinmentData?.selectedProduct;
  const _ptIsGp = _ptCat?.type === 'gp' || _ptCat?.product_category_id == 1;
  const _ptIsReshedula = props.showAppoinmentData?.clinic?.isReshedula;
  const _ptAllowLang = _ptIsGp || _ptIsReshedula || _ptProd?.allow_language_selection || _ptCat?.allow_language_selection;
  const _ptAllowGender = _ptIsGp || _ptIsReshedula || _ptProd?.allow_gender_selection || _ptCat?.allow_gender_selection;

  if (_ptAllowLang && props.showAppoinmentData && props.showAppoinmentData.language) {
    let data = '';
    if (props.showAppoinmentData.language && props.showAppoinmentData.language.value) {
      data = props.showAppoinmentData.language.value;
    } else {
      data = props.showAppoinmentData.language;
    }
    let languageName = props.data.languageData.find(landata => landata.value == data);
    const _ptLangLabel = languageName ? languageName.label : data;
    if (_ptLangLabel && String(_ptLangLabel).toLowerCase() !== 'english') {
      tempDataForDuration.push({ key: 'Language', data: _ptLangLabel });
    }
  }
  if (_ptAllowGender && props.showAppoinmentData && props.showAppoinmentData.gender) {
    tempDataForDuration.push({
      key: 'Gender',
      data: props.showAppoinmentData.gender ? formatString(props.showAppoinmentData.gender) : ''
    });
  }

  // ── formatChipLabel ────────────────────────────────────────────────────────
  const formatChipLabel = (dStr) => {
    if (!dStr) return '';
    const d = dayjs(dStr);
    return d.isValid() ? d.format('ddd, DD MMM') : dStr;
  };

  // ── Initial load: fetch available slots (unified get-available-slots call) ───
  useEffect(() => {
    getAllPreferedTimeSlot(1, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevSlotsLenRef = useRef(0);
  // Set when chosen dates are cleared, so the *next* showPreferredTimeSlot
  // update (the fresh default-view fetch triggered by that clear) re-opens the
  // first 3 dates using up-to-date data — chosenDates changing fires before
  // that fetch resolves, so re-opening immediately there would act on stale,
  // date-scoped data still left over from before the clear.
  const justClearedDatesRef = useRef(false);
  const prevChosenDatesSizeRef = useRef(0);
  // Auto-open dates: first 3 on fresh load (or after clearing chosen dates
  // back to browsing mode), or exactly the chosen dates — and for the latter,
  // default each to the "All day" filter so results show immediately instead
  // of requiring an extra manual click per date.
  useEffect(() => {
    const currentLen = showPreferredTimeSlot ? Object.keys(showPreferredTimeSlot).length : 0;
    if (chosenDates.size > 0) {
      setOpenDates(new Set(chosenDates));
      setDateFilters(prev => {
        const next = { ...prev };
        chosenDates.forEach(d => { if (!next[d]) next[d] = 'all'; });
        return next;
      });
    } else if ((currentLen > 0 && prevSlotsLenRef.current === 0) || justClearedDatesRef.current) {
      justClearedDatesRef.current = false;
      setOpenDates(new Set(Object.keys(showPreferredTimeSlot).slice(0, 3)));
    }
    prevSlotsLenRef.current = currentLen;
    if (chosenDates.size === 0 && prevChosenDatesSizeRef.current > 0) {
      justClearedDatesRef.current = true;
    }
    prevChosenDatesSizeRef.current = chosenDates.size;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreferredTimeSlot, chosenDates]);

  // ── Location search debounce ───────────────────────────────────────────────
  useEffect(() => {
    if (debouncedInputValueData) {
      getAllSearchData(debouncedInputValueData);
    }
  }, [debouncedInputValueData]);

  // ── API: location text search ──────────────────────────────────────────────
  const getAllSearchData = async (payload) => {
    if (payload && payload.split(' ').join('').length >= 3) {
      setloadingLocation(true);
      pushTraceLogEvent({
        event: "address_search_api_called",
        payload: { search_query: payload, step: "preferred_time_clinic_selection" },
        step_status: "success",
      });
      try {
        const csrRoomData = await spacerData('get-address', { input: payload });
        if (csrRoomData?.data?.data) {
          const tempData = csrRoomData.data.data.map(a => ({ value: `${a.lat}::${a.lng}`, label: a.address }));
          setsearchBylocationOption(tempData);
          pushTraceLogEvent({
            event: "address_search_results_received",
            payload: { search_query: payload, result_count: tempData.length, step: "preferred_time_clinic_selection" },
            step_status: "success",
          });
        }
      } catch (e) {}
      setloadingLocation(false);
    }
  };

  const onSelectLocation = (data) => {
    const parts = data.split('::');
    if (parts.length === 2) {
      const coords = { lat: parts[0], lng: parts[1] };
      setlocationCoords(coords);
      getAllPreferedTimeSlot(1, true, coords);
    }
    const selectedLabel = searchBylocationOption?.find(opt => opt.value === data)?.label ?? data;
    pushTraceLogEvent({
      event: "address_selected_from_dropdown",
      payload: { selected_address: selectedLabel, selected_value: data, step: "preferred_time_clinic_selection" },
      step_status: "success",
    });
  };

  // ── API: unified available-slots fetch (IOO-324 v2 — /api/admin/v2/get-available-slots) ──
  // Replaces the old two-call preferred-clinics + preferred-time/page/{n} pattern.
  // The right-panel clinic list is now DERIVED from this same response (no separate
  // preferred-clinics call) since preferred_by="time" already nests each date's
  // clinics + their own slots directly — no more cross-referencing available_clinics.
  // This endpoint has no `preferred_dates` param (only a start/end range) — when the
  // admin picks 1-2 specific dates we fire one call per date and merge, rather than
  // trusting a single range call (which would misbehave if the 2 dates are >10 days apart).
  const buildAvailableSlotsPayload = (preferredBy, coords, dateOverride) => {
    const catType = props.showAppoinmentData?.clinic?.productCategory?.type;
    const _pid = props.showAppoinmentData?.clinic?.productCategory?.product_category_id;
    let data = { preferred_by: preferredBy, product_category_id: _pid ?? 1 };
    if (catType !== 'gp') {
      data.product_id = props.showAppoinmentData?.clinic?.non_gp_product_id ?? null;
    } else {
      data.duration_id = props.showAppoinmentData?.duration ?? 1;
    }
    const isReshedula = props.showAppoinmentData?.clinic?.isReshedula;
    const calenderData = props.showAppoinmentData?.clinic?.calenderData;
    if (isReshedula && calenderData?.appointment_id) {
      data.appointment_id = calenderData.appointment_id;
    }
    const rescheduleGender = props.showAppoinmentData?.gender ?? calenderData?.preferred_gender;
    const effectiveGender = isReshedula ? rescheduleGender : props.showAppoinmentData?.gender;
    if (effectiveGender && effectiveGender !== 'no_preference') data.gender = effectiveGender;
    if (props.showAppoinmentData?.language) {
      let id = props.showAppoinmentData?.language;
      if (props.showAppoinmentData?.language?.label === 'English') id = 1;
      data.languages = [id];
    }
    if (coords?.lat && coords?.lng) { data.lat = coords.lat; data.lng = coords.lng; }
    const session_id = localStorage.getItem("bookingSessionId");
    if (session_id && session_id !== 'undefined') data.slot_session_id = session_id;
    if (dateOverride) { data.start_date = dateOverride; data.end_date = dateOverride; }
    return data;
  };

  // Derive the right-panel clinic list from the date-keyed available_dates map.
  const deriveClinicsFromDates = (datesMap) => {
    const byId = new Map();
    Object.keys(datesMap).sort().forEach(day => {
      (datesMap[day] || []).forEach(entry => {
        if (!byId.has(String(entry.clinic_id))) {
          const meta = props.data?.allClinicDataByID?.[entry.clinic_id] || {};
          byId.set(String(entry.clinic_id), {
            ...meta,
            clinic_id: entry.clinic_id,
            clinic_name: entry.clinic_name,
            distance: entry.distance,
            clinic_availability_status: entry.clinic_availability_status,
            next_available_slot: entry.next_available_slot,
            id: entry.clinic_id,
            name: entry.clinic_name,
          });
        }
      });
    });
    return Array.from(byId.values());
  };

  // Guards against out-of-order / duplicate responses (e.g. React StrictMode's
  // dev-only double-invoke of effects, or the admin changing location/dates
  // faster than a previous request resolves) overwriting newer state with stale data.
  const fetchSeqRef = useRef(0);

  const getAllPreferedTimeSlot = async (pageNumber = 1, reset = true, coordsOverride = null, datesOverride = null) => {
    const seq = ++fetchSeqRef.current;
    const coords = coordsOverride ?? locationCoords;
    const activeDates = datesOverride !== null ? datesOverride : Array.from(chosenDates);
    if (!loadingMore) setloadingTime(true);
    if (reset && pageNumber === 1) setloading(true);
    try {
      let mergedDates = reset ? {} : showPreferredTimeSlot;
      let nextMeta = metaData;
      // Unlike mergedDates/available_dates, this represents the full universe of
      // bookable dates for this category/product — not scoped to the current
      // pagination or date-filter query — so it must never be cleared on reset
      // (clearing it here would break the date-picker's availability restriction
      // the moment the admin picks a specific date, since that fetch is reset:true).
      let mergedAllAvailableDates = new Set(allAvailableDates);

      if (activeDates.length > 0) {
        const responses = await Promise.all(activeDates.map(d => genericApiCall(
          "get-available-slots/page/1",
          buildAvailableSlotsPayload("time", coords, d),
          Config.urlV2,
          "available_slots_fetched",
        )));
        responses.forEach(res => {
          mergedDates = { ...mergedDates, ...(res?.data?.data?.available_dates || {}) };
          (res?.data?.data?.all_available_dates || []).forEach(d => mergedAllAvailableDates.add(d));
        });
        nextMeta = { current_page: 1, last_page: 1 };
      } else {
        const res = await genericApiCall(
          `get-available-slots/page/${pageNumber}`,
          buildAvailableSlotsPayload("time", coords, null),
          Config.urlV2,
          "available_slots_fetched",
        );
        mergedDates = { ...mergedDates, ...(res?.data?.data?.available_dates || {}) };
        (res?.data?.data?.all_available_dates || []).forEach(d => mergedAllAvailableDates.add(d));
        nextMeta = { current_page: res?.data?.current_page ?? pageNumber, last_page: res?.data?.last_page ?? pageNumber };
      }

      if (seq !== fetchSeqRef.current) return; // a newer fetch has since started — discard this stale response
      setshowPreferredTimeSlot(mergedDates);
      setSelectedClinicsNew(deriveClinicsFromDates(mergedDates));
      setAllAvailableDates(Array.from(mergedAllAvailableDates));
      setmetaData(nextMeta);
    } catch (error) {}
    setloading(false);
    setloadingTime(false);
    setLoadingMore(false);
  };

  // ── Slot selection (single slot across the whole booking) ─────────────────
  const pickSlot = (clinicId, info, date) => {
    const id = `${date}${info.start_time}`;
    const isSameSlot = bookedSlot && bookedSlot.id === id && bookedSlot.clinicId === clinicId;

    const next = isSameSlot ? null : {
      id,
      dateStr: date,
      clinicId,
      end_time: info.end_time,
      start_time: info.start_time,
      info,
      date,
    };
    setBookedSlot(next);

    pushTraceLogEvent({
      event: isSameSlot ? "unselected_preferred_time" : "selected_preferred_time",
      payload: { selected_slot: `${info.start_time}-${info.end_time}`, day: date, clinic_id: clinicId },
      step_status: "success",
    });

    props.callback && props.callback({
      selectedClinics: next ? [clinicId] : [],
      selectedSlots: next ? [next] : [],
      type: 'PreferredTime',
    }, 'PreferredTime');
  };

  // ── Date accordion toggle (left panel) ─────────────────────────────────────
  const toggleDate = (dateStr) => {
    setOpenDates(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) {
        next.delete(dateStr);
      } else {
        logInteraction("date_card_expanded", { date: dateStr });
        next.add(dateStr);
      }
      return next;
    });
  };

  const setDateFilter = (dateStr, val) => {
    const isCurrentlyActive = !!dateFilters[dateStr];
    const nextVal = dateFilters[dateStr] === val ? null : val;
    // Cap at 3 dates with an active time-of-day filter at once — mirrors the
    // 3-clinic cap in ClinicBooking.js. Only applies when activating a *new*
    // date; switching an already-active date's filter (or clearing it) is fine.
    if (!isCurrentlyActive && nextVal) {
      const activeCount = Object.values(dateFilters).filter(Boolean).length;
      if (activeCount >= 3) {
        messageApi.error({ type: "error", content: "You can only select up to 3 dates." });
        return;
      }
    }
    logInteraction("am_pm_filter_applied", { filter: nextVal, date: dateStr });
    setDateFilters(prev => ({ ...prev, [dateStr]: nextVal }));
    // Changing the time-of-day band invalidates a booked slot for this date.
    setBookedSlot(prev => (prev && prev.dateStr === dateStr ? null : prev));
  };

  // Does ANY clinic have a slot for `day` matching its chosen filter? Once a
  // filter is picked for a date, dates with zero matching slots (across every
  // clinic) are hidden entirely from the left panel rather than shown empty.
  const dayHasAnyMatch = (day, filterVal) => {
    if (!filterVal) return true;
    const entries = showPreferredTimeSlot[day] || [];
    return entries.some(entry => applyTimeFilter(entry.slots || [], filterVal).length > 0);
  };

  // Does this specific clinic have a slot matching the filter for at least one
  // of the currently filter-active dates? Used to hide clinics with zero
  // matching availability from the right panel entirely, rather than listing
  // them with an empty/"No slots available" nest.
  const clinicMatchesActiveFilters = (idStr, activeDays) => {
    return activeDays.some(day => {
      const dateEntry = (showPreferredTimeSlot[day] || []).find(c => String(c.clinic_id) === idStr);
      return applyTimeFilter(dateEntry?.slots || [], dateFilters[day]).length > 0;
    });
  };

  // ── Clinic card accordion toggle (right panel) ──────────────────────────────
  const toggleClinicCard = (clinicId) => {
    setOpenClinicCards(prev => {
      const next = new Set(prev);
      if (next.has(clinicId)) {
        next.delete(clinicId);
      } else {
        logInteraction("clinic_card_expanded", { clinic_id: clinicId });
        next.add(clinicId);
      }
      return next;
    });
  };

  // ── Load more ──────────────────────────────────────────────────────────────
  const getLoadMoreData = () => {
    if (metaData && metaData.current_page && metaData.current_page < metaData.last_page) {
      logInteraction("load_more_clicked", {
        current_page: metaData.current_page,
        next_page: 1 + parseInt(metaData.current_page),
        last_page: metaData.last_page,
        screen: "preferred_time",
      });
      setLoadingMore(true);
      getAllPreferedTimeSlot(1 + parseInt(metaData.current_page), false, locationCoords, []);
    }
  };

  const canContinue = bookedSlot !== null;

  // ── Continue: block the picked slot then advance to patient details ───────
  // NOTE: this screen lets the admin pick clinic+slot in one step, so Continue
  // passes type="AppointmentSelection" straight to the orchestrator's callback to
  // skip the old AppointmentSelection.js grid-picker screen (ClinicCalendarPage.js
  // renders BookingDetailsNext when showAppoinmentDataDisplay=="AppointmentSelection",
  // not the AppointmentSelection component). That skip previously also skipped the
  // block-slot call that used to live inside that screen's own Continue handler —
  // meaning the slot was never actually held server-side. Fixed by calling it here.
  const handleContinueButton = async () => {
    if (!canContinue) return;
    const clinicId = bookedSlot.clinicId;
    const clinicData = selectedClinicsNew.find(c => (c.id || c.clinic_id) == clinicId) || { id: clinicId, name: 'Selected Clinic' };
    const productCategory = props.showAppoinmentData?.clinic?.productCategory;
    const isHS = productCategory?.type === 'd2c' || productCategory?.type === 'corporate';
    const payload = {
      clinic_id: clinicData.id,
      start_time: moment(`${bookedSlot.date} ${bookedSlot.start_time}`).format("YYYY-MM-DD HH:mm"),
      end_time: moment(`${bookedSlot.date} ${bookedSlot.end_time}`).format("YYYY-MM-DD HH:mm"),
      product_category_id: productCategory?.product_category_id,
      ...(isHS && { product_id: Number(props.showAppoinmentData?.clinic?.non_gp_product_id) }),
    };

    setBlockingSlot(true);
    try {
      const existingSession = localStorage.getItem("bookingSessionId");
      if (existingSession) payload.session_id = existingSession;
      localStorage.removeItem("bookingSessionId");
      localStorage.removeItem("bookingExpiresAt");

      const res = await genericApiCall("block-slot", payload);

      if (Array.isArray(res) && !res.length) {
        pushTraceLogEvent({
          event: "block_slot_failed",
          payload: { clinic_id: clinicData.id, start_time: payload.start_time, end_time: payload.end_time, reason: "empty_response" },
          step_status: "failed",
        });
        throw new Error(res.data?.message || "This slot is already selected by another user. Please choose another slot.");
      }

      const session_id = res.data?.data?.session_id ?? res.data?.session_id;
      const expires_at = res.data?.data?.expires_at ?? res.data?.expires_at;
      if (session_id) {
        localStorage.setItem("bookingSessionId", session_id);
        if (expires_at) localStorage.setItem("bookingExpiresAt", expires_at);
      } else {
        console.warn("[PreferredTime] block-slot response missing session_id — slot_session_id will not be available downstream.", res.data);
      }
      pushTraceLogEvent({
        event: "slot_blocked",
        payload: {
          clinic_id: clinicData.id,
          clinic_name: clinicData.name,
          start_time: payload.start_time,
          end_time: payload.end_time,
          product_category_id: payload.product_category_id,
          date: new Date().toISOString().split("T")[0],
          slot_session_id: session_id,
        },
        step_status: "success",
      });

      const payloadPassNew = {
        selectedClinics: [clinicId],
        slot: { date: bookedSlot.date, info: bookedSlot.info, clinicData },
        type: "PreferredTime",
      };
      props.callback && props.callback(payloadPassNew, "AppointmentSelection");
      pushTraceLogEvent({
        event: "preferred_time_continue_clicked",
        payload: { selected_slot: bookedSlot, selected_clinics: [clinicId] },
        step_status: "success",
      });
    } catch (err) {
      messageApi.error({ content: err.message || "Unable to block the slot." });
    } finally {
      setBlockingSlot(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="ptv2-root">
      {contextHolder}

      {/* Booking Summary Header */}
      <div style={{ display: 'grid', justifyContent: 'center' }}>
        <div style={{ width: '605px', background: "#00838540", padding: 10, marginTop: 40 }}>
          <Title style={{ margin: 0, fontSize: 20, color: '#008385', fontWeight: "400" }}>My booking:</Title>
        </div>
      </div>
      <div className="summuryCard" style={{ width: 394, marginTop: 20, flexDirection: "column", alignItems: "center", gap: 0 }}>
        <span style={{ whiteSpace: "pre-line", textAlign: "center" }}>
          {props?.showAppoinmentData.clinic?.productCategory?.name
            ? props?.showAppoinmentData.clinic?.productCategory?.name
            : props?.showAppoinmentData.clinic?.clinic?.productCategory?.name
              ? props?.showAppoinmentData.clinic?.clinic?.productCategory?.name
              : ''}
          {props?.showAppoinmentData.clinic?.productCategory?.type === 'gp' ? "\n" : ''}
          {(props?.showAppoinmentData.clinic?.productCategory?.type === 'd2c' || props?.showAppoinmentData.clinic?.productCategory?.type === 'corporate') ? <br /> : ''}
          {props?.showAppoinmentData?.clinic?.productCategory?.type === 'd2c' && props?.showAppoinmentData?.clinic?.non_gp_product_id && props?.getAllHealthScreenData?.getAllHealthScreenData?.data
            ? filterByNonGpProductId(props.getAllHealthScreenData.getAllHealthScreenData.data, props?.showAppoinmentData.clinic.non_gp_product_id)?.[0]?.name || ''
            : ''}
          {props?.showAppoinmentData?.clinic?.productCategory?.type === 'corporate'
            ? filterByNonGpProductId(props?.showAppoinmentData?.clinic?.selectedCorporate?.corporate_products, props?.showAppoinmentData?.clinic?.non_gp_product_id)?.[0]?.name || ''
            : ''}
        </span>
        {(() => {
          const visibleChips = tempDataForDuration.filter(rs => {
            if (rs.key === 'Gender' && rs.data.toLowerCase() === 'no preference') return false;
            return true;
          });
          if (!visibleChips.length) return null;
          return (
            <div style={{ fontSize: 13, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0 4px' }}>
              {visibleChips.map((rs, i) => (
                <React.Fragment key={rs.key || i}>
                  <span style={{ color: '#000' }}>{rs.key}:&nbsp;<span style={{ color: '#008385', fontWeight: 500 }}>{rs.data}</span></span>
                  {i !== visibleChips.length - 1 && <span style={{ color: '#000', margin: '0 6px' }}>|</span>}
                </React.Fragment>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Two-Column Grid */}
      <div className="ptv2-columns" style={{ marginTop: '1rem' }}>

        {/* LEFT PANEL: Preferred time */}
        <div className="ptv2-panel">
          <div className="ptv2-ph">
            <span className="ptv2-ptitle">Preferred time</span>
          </div>

          {/* Date Selection Bar */}
          <div style={{ padding: '0.6rem 1rem', borderBottom: '1px solid var(--pt-border)', flexShrink: 0 }}>
            <div style={{ fontSize: 14, color: 'var(--pt-text-secondary)', marginBottom: 8 }}>
              {chosenDates.size > 0 ? 'Showing results for selected dates' : 'Select up to 2 preferred dates'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {[...chosenDates].map(dStr => (
                  <span key={dStr} className="ptv2-fchip on" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {formatChipLabel(dStr)}
                    <span style={{ cursor: 'pointer', fontSize: 12 }} onClick={() => {
                      const next = new Set(chosenDates);
                      next.delete(dStr);
                      setChosenDates(next);
                      logInteraction(next.size > 0 ? "preferred_dates_selected" : "preferred_dates_cleared", {
                        removed_date: dStr,
                        preferred_dates: Array.from(next),
                      });
                      getAllPreferedTimeSlot(1, true, locationCoords, Array.from(next));
                    }}>✕</span>
                  </span>
                ))}
              </div>
              <div className="dp-popover-wrapper">
                <button className="dp-trigger-btn" onClick={() => {
                  logInteraction("date_picker_opened", { currently_open: !dpOpen });
                  setDpOpen(o => !o);
                }}>
                  <CalendarIcon /> {chosenDates.size > 0 ? `${chosenDates.size}/2 dates` : 'Select dates'}
                </button>
                <DatePickerPopover
                  open={dpOpen}
                  onClose={() => {
                    setDpOpen(false);
                    // Fetch once, using whatever dates ended up chosen — not on
                    // every individual date click while the popover is still open.
                    getAllPreferedTimeSlot(1, true, locationCoords, Array.from(chosenDates));
                  }}
                  chosenDates={chosenDates}
                  maxDates={2}
                  availableDates={allAvailableDates}
                  onDateToggle={(ymd) => {
                    const next = new Set(chosenDates);
                    if (next.has(ymd)) next.delete(ymd);
                    else if (next.size < 2) next.add(ymd);
                    setChosenDates(next);
                    logInteraction("preferred_dates_selected", { preferred_dates: Array.from(next) });
                  }}
                  onClearAll={() => {
                    setChosenDates(new Set());
                    logInteraction("preferred_dates_cleared", {});
                    getAllPreferedTimeSlot(1, true, locationCoords, []);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Scrollable Date Cards */}
          <div className="ptv2-scroll-body">
            {loadingTime && !loadingMore ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}><Spin className="custom-spinner" /></div>
            ) : !showPreferredTimeSlot || Object.keys(showPreferredTimeSlot).length === 0 ? (
              <div className="cbv2-empty-state">No available slots found.</div>
            ) : (() => {
              const visibleDays = Object.keys(showPreferredTimeSlot).filter(day =>
                dayHasAnyMatch(day, dateFilters[day] || null)
              );
              if (visibleDays.length === 0) {
                return <div className="cbv2-empty-state">No availability for the selected time of day.</div>;
              }
              return visibleDays.map(day => {
                const isDateOpen = openDates.has(day);
                const dateFilterVal = dateFilters[day] || null;
                const hasPick = bookedSlot?.dateStr === day;

                return (
                  <div key={day} className="ptv2-date-card">
                    <div
                      className={`ptv2-date-card-header${isDateOpen ? ' is-open' : ''}${hasPick ? ' has-pick' : ''}`}
                      onClick={() => toggleDate(day)}
                    >
                      <span className="ptv2-dchip">{moment(day).format('ddd, DD MMM')}</span>
                      <span className="ptv2-chevron">{isDateOpen ? '▲' : '▼'}</span>
                    </div>

                    {isDateOpen && (
                      <div className="ptv2-date-filter-body">
                        <div className="ptv2-filter-row">
                          <span className="ptv2-filter-label">Time of day:</span>
                          {FILTER_OPTS.map(f => (
                            <div
                              key={f.val}
                              className={`ptv2-fchip${dateFilterVal === f.val ? ' on' : ''}`}
                              onClick={() => setDateFilter(day, f.val)}
                            >
                              {f.label}
                            </div>
                          ))}
                        </div>
                        {!dateFilterVal && (
                          <div className="ptv2-nest-empty">Select a time of day to see available slots.</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              });
            })()}

            {/* Load more spinner */}
            {loadingMore && <Spin className="custom-spinner" style={{ display: 'block', textAlign: 'center', marginTop: 12 }} />}

            {/* Load More button */}
            {chosenDates.size === 0 && !loadingTime && showPreferredTimeSlot && Object.keys(showPreferredTimeSlot).length > 0 && (
              <div className="ptv2-load-more-wrap">
                {loadingMore ? (
                  <Spin size="small" style={{ color: '#008385' }} />
                ) : metaData?.current_page < metaData?.last_page ? (
                  <button className="ptv2-load-more-btn" onClick={getLoadMoreData}>
                    Load More <span>∨</span>
                  </button>
                ) : (
                  <span style={{ fontSize: 13, color: '#008385', fontWeight: 500 }}>
                    ✓ All available slots loaded
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL: Preferred clinic */}
        <div className="ptv2-panel">
          <div className="ptv2-ph">
            <span className="ptv2-ptitle">Preferred clinic</span>
          </div>

          {/* Location search */}
          <div className="ptv2-loc-actions">
            <div
              className={`ptv2-loc-btn${locationPermission === 'granted' ? ' enabled' : ''}`}
              onClick={handleEnableLocation}
            >
              <LocationIcon />
              <span>{locationPermission === 'granted' ? 'Enabled' : 'Enable location'}</span>
            </div>

            <span className="ptv2-or-label">OR</span>

            <div style={{ width: '100%' }}>
              <LocationSearchSelect
                options={searchBylocationOption}
                loading={loadingLocation}
                searchValue={searchBylocationName}
                onSelect={onSelectLocation}
                onSearch={(value) => {
                  setsearchBylocationName(value);
                  // Show the loader from the moment a real search kicks in (not
                  // just during the network call) — the actual fetch only starts
                  // after an 800ms debounce, so waiting for getAllSearchData's own
                  // setloadingLocation(true) left the spinner's visible window too
                  // short to notice.
                  setloadingLocation(!!(value && value.split(' ').join('').length >= 3));
                }}
              />
            </div>
          </div>

          <div className="ptv2-clinic-hint">Select one appointment slot with any of the clinics below</div>

          {/* Clinic list */}
          <div className="cbv2-clinic-list">
            {!anyFilterActive ? (
              <div className="ptv2-nest-empty">
                Select All day / AM / PM for a date on the left to see available clinics.
              </div>
            ) : selectedClinicsNew && selectedClinicsNew.length ? (() => {
              const openDatesWithFilter = Object.keys(showPreferredTimeSlot || {}).filter(day => dateFilters[day]);
              const clinicEntries = [...selectedClinicsNew]
                .filter(a => a.status != "0")
                .map(a => {
                  const isUnavailable = a.clinic_availability_status === false;
                  const idStr = (a.clinic_id ?? a.id)?.toString();
                  const hasMatch = isUnavailable ? false : clinicMatchesActiveFilters(idStr, openDatesWithFilter);
                  return { a, isUnavailable, hasMatch };
                });
              // Browsing without specific dates chosen: hide clinics with no
              // matching slots entirely — with many clinics in the list, showing
              // every one with an empty tag would be noisy. Once specific dates
              // are chosen via the picker, keep every clinic visible so the admin
              // can compare, tagging the unavailable ones instead of hiding them.
              const visibleEntries = chosenDates.size > 0
                ? clinicEntries
                : clinicEntries.filter(({ isUnavailable, hasMatch }) => isUnavailable || hasMatch);

              const sortedEntries = visibleEntries.sort((x, y) => {
                const aU = x.isUnavailable, bU = y.isUnavailable;
                return aU === bU ? 0 : aU ? 1 : -1;
              });

              if (sortedEntries.length === 0) {
                return <div className="cbv2-empty-state">No clinics available for the selected time of day.</div>;
              }

              return sortedEntries.map(({ a, isUnavailable, hasMatch }) => {
                  if (a.clinic_id) a.id = a.clinic_id;
                  const idStr = a.id?.toString();
                  const hasPick = bookedSlot?.clinicId === idStr;
                  const isCardOpen = openClinicCards.has(idStr);
                  const canExpand = !isUnavailable && hasMatch;
                  const matchingDays = openDatesWithFilter.filter(day => {
                    const dateEntry = (showPreferredTimeSlot[day] || []).find(c => String(c.clinic_id) === idStr);
                    return applyTimeFilter(dateEntry?.slots || [], dateFilters[day]).length > 0;
                  });

                  return (
                    <div key={a.id} className="ptv2-clinic-card">
                      <div
                        className={`ptv2-clinic-row${hasPick ? ' active' : ''}${isUnavailable ? ' unavail' : ''}${isCardOpen ? ' open' : ''}`}
                        onClick={() => canExpand && toggleClinicCard(idStr)}
                        onMouseEnter={() => canExpand && setHoveredClinic(a)}
                        onMouseLeave={() => setHoveredClinic(null)}
                      >
                        <div className="ptv2-clinic-main">
                          <div className="ptv2-clinic-name">{a.name}</div>
                        </div>
                        {a.distance != null && (
                          <span className="ptv2-distance-chip">{a.distance}</span>
                        )}
                        {isUnavailable ? (
                          <span className="cbv2-unavail-tag">Unavailable</span>
                        ) : !hasMatch ? (
                          <span className="ptv2-next-chip">No availability on selected date{chosenDates.size > 1 ? 's' : ''}</span>
                        ) : (
                          <span className="ptv2-next-chip">{a.address}</span>
                        )}
                        {canExpand && (
                          <span className="ptv2-chevron" style={{ marginLeft: 4 }}>
                            {isCardOpen ? '▲' : '▼'}
                          </span>
                        )}
                      </div>

                      {/* Date Nest (Inside Expanded Clinic Card) */}
                      {canExpand && isCardOpen && (
                        <div className="ptv2-clinic-dates-nest">
                          {matchingDays.length === 0 ? (
                            <div className="ptv2-nest-empty">
                              Select All day / AM / PM on the left for a date to see availability here.
                            </div>
                          ) : (
                            matchingDays.map(day => {
                              const dateEntry = (showPreferredTimeSlot[day] || []).find(c => String(c.clinic_id) === idStr);
                              const clinicSlots = dateEntry?.slots || [];
                              const filterVal = dateFilters[day];
                              const filtered = applyTimeFilter(clinicSlots, filterVal);
                              const groups = groupSlotsByAmPm(filtered);
                              const pickedHere = bookedSlot && bookedSlot.dateStr === day && bookedSlot.clinicId === idStr
                                ? bookedSlot.start_time
                                : null;

                              return (
                                <div key={day} className="ptv2-clinic-date-item">
                                  <div className="ptv2-clinic-date-label">
                                    {moment(day).format('ddd, DD MMM')}
                                    {pickedHere && <span className="ptv2-picked-badge">{pickedHere}</span>}
                                  </div>

                                  {filterVal === 'all' ? (
                                    groups.map(g => (
                                      <React.Fragment key={g.label}>
                                        <div className="ptv2-slot-group-label">{g.label}</div>
                                        <div className="ptv2-slots-grid">
                                          {g.slots.map(slot => {
                                            const isSelected = pickedHere === slot.start_time;
                                            const isSlotUnavailable = slot.availablity_status == 0;
                                            return (
                                              <div
                                                key={`${day}${slot.start_time}-${slot.end_time}`}
                                                className={`ptv2-slot${isSelected ? ' picked' : ''}`}
                                                style={{ opacity: isSlotUnavailable ? 0.4 : 1, cursor: isSlotUnavailable ? 'not-allowed' : 'pointer' }}
                                                onClick={() => !isSlotUnavailable && pickSlot(idStr, slot, day)}
                                              >
                                                {slot.start_time}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </React.Fragment>
                                    ))
                                  ) : (
                                    <div className="ptv2-slots-grid">
                                      {filtered.map(slot => {
                                        const isSelected = pickedHere === slot.start_time;
                                        const isSlotUnavailable = slot.availablity_status == 0;
                                        return (
                                          <div
                                            key={`${day}${slot.start_time}-${slot.end_time}`}
                                            className={`ptv2-slot${isSelected ? ' picked' : ''}`}
                                            style={{ opacity: isSlotUnavailable ? 0.4 : 1, cursor: isSlotUnavailable ? 'not-allowed' : 'pointer' }}
                                            onClick={() => !isSlotUnavailable && pickSlot(idStr, slot, day)}
                                          >
                                            {slot.start_time}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                });
            })() : loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}><Spin className="custom-spinner" /></div>
            ) : (
              <div className="cbv2-empty-state">No clinics found</div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="ptv2-bottom-bar">
        <button
          className="ptv2-btn-back"
          onClick={() => {
            props.callBackForbackpage && props.callBackForbackpage('BookingForm');
            pushTraceLogEvent({
              event: "navigate_to_appointment_duration_selection",
              payload: { action: "navigate to appointment duration selection" },
              step_status: "success",
            });
          }}
        >
          ← Back
        </button>
        <button
          className="ptv2-btn-cont"
          disabled={!canContinue || blockingSlot}
          onClick={handleContinueButton}
        >
          {blockingSlot ? "Booking..." : "Continue"}
        </button>
      </div>
    </div>
  );
};

export default PreferredTime;
